import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { asset } from '../assets';
import { validateCustomerDetails } from '../customerDetails';
import { clearEditingQuote, editingQuotePath, readEditingQuote, saveEditingQuote } from '../quoteEdit';
import { getPublicErrorMessage } from '../publicError';
import { createQuoteRequestRows, hasValidQuoteQuantity } from '../quoteItem';
import { localizeQuoteProduct, localizeQuoteSummary } from '../quoteLocalization';
import {
  clearQuoteUploadFiles,
  getQuoteUploadFiles,
  quoteUploadValidationError,
  removeQuoteUploads,
  setQuoteUploadFiles,
  uploadQuoteDesignFiles,
} from '../quoteUploads';
import { supabaseClient } from '../supabase';
import { useAuth } from './AuthProvider';
import { useLanguage } from './LanguageProvider';

const CART_STORAGE_KEY = 'ingaz-quote-cart';
const CONTACT_FIELDS = ['name', 'email', 'phone'];
const EMPTY_CONTACT = { name: '', email: '', phone: '' };
const QuoteCartContext = createContext(null);

function readCart() {
  try {
    const savedCart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || '[]');
    return Array.isArray(savedCart) ? savedCart : [];
  } catch {
    return [];
  }
}

function QuoteCartDrawer({ cart, checkoutCart, closeCart, editItem, isOpen, removeItem, submitting, uploading }) {
  const { language, t } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className="quote-overlay" onClick={(event) => event.target === event.currentTarget && closeCart()} role="presentation">
      <aside aria-label="Quote cart" className="quote-cart">
        <div className="quote-panel-header">
          <div>
            <h2>{t('Quote Cart', 'سلة عروض الأسعار')}</h2>
            <p>{t(`${cart.length} item${cart.length === 1 ? '' : 's'}`, `${cart.length} عناصر`)}</p>
          </div>
          <button aria-label="Close cart" className="quote-close" onClick={closeCart} type="button">&times;</button>
        </div>

        <div className="quote-cart-items">
          {cart.length === 0 ? (
            <p className="quote-empty">{t('Your quote cart is empty.', 'سلة عروض الأسعار فارغة.')}</p>
          ) : cart.map((item) => (
            <article className="quote-cart-item" key={item.id}>
              <div className="quote-cart-item-content">
                <h3>{localizeQuoteProduct(item.productType, language)}</h3>
                <p>{t('Quantity', 'الكمية')}: {item.quantity}</p>
                {localizeQuoteSummary(item, language) && <small>{localizeQuoteSummary(item, language)}</small>}
              </div>
              <div className="quote-cart-item-actions">
                <button
                  aria-label={`${t('Edit', 'تعديل')} ${item.productType}`}
                  className="quote-cart-item-action quote-cart-item-edit"
                  onClick={() => editItem(item)}
                  title={t('Edit quote', 'تعديل عرض السعر')}
                  type="button"
                >
                  <img aria-hidden="true" src={asset('assets/edit.svg')} alt="" />
                </button>
                <button
                  aria-label={`${t('Remove', 'حذف')} ${item.productType}`}
                  className="quote-cart-item-action quote-cart-item-remove"
                  onClick={() => removeItem(item.id)}
                  title={t('Remove from cart', 'حذف من السلة')}
                  type="button"
                >
                  <img aria-hidden="true" src={asset('assets/trash.svg')} alt="" />
                </button>
              </div>
            </article>
          ))}
        </div>

        {cart.length > 0 && (
          <button className="btn2 quote-checkout-button" disabled={submitting} onClick={checkoutCart} type="button">
            {uploading
              ? t('Uploading files...', 'جاري رفع الملفات...')
              : submitting
                ? t('Submitting...', 'جاري الإرسال...')
                : t('Checkout All Quotes', 'إرسال كل عروض الأسعار')}
            <img aria-hidden="true" className="btn-arrow" src={asset('assets/arrow.svg')} alt="" />
          </button>
        )}
      </aside>
    </div>
  );
}

function ContactCheckoutModal({
  closeCheckout,
  contact,
  fields,
  isOpen,
  isProfileCompletion,
  submitCheckout,
  submitting,
  uploading,
}) {
  const { t } = useLanguage();
  const [values, setValues] = useState(contact);

  useEffect(() => {
    if (isOpen) setValues(contact);
  }, [contact, isOpen]);

  const validation = validateCustomerDetails(values);
  const canSubmit = fields.every((fieldName) => !validation.errors[fieldName]);

  if (!isOpen) return null;

  const submit = (event) => {
    event.preventDefault();
    if (!canSubmit || submitting) return;
    submitCheckout(validation.normalized);
  };

  const renderField = (name, label, autoComplete, type) => {
    if (!fields.includes(name)) return null;

    const error = validation.errors[name];
    const id = `quoteCustomer${name[0].toUpperCase()}${name.slice(1)}`;

    return (
      <div className={`form-group${error ? ' has-error' : ''}`}>
        <label htmlFor={id}>{label} <span className="required-mark" aria-hidden="true">*</span></label>
        <input
          aria-describedby={error ? `${id}Error` : undefined}
          aria-invalid={Boolean(error)}
          autoComplete={autoComplete}
          id={id}
          name={name}
          onChange={(event) => setValues((current) => ({ ...current, [name]: event.target.value }))}
          required
          type={type}
          value={values[name] || ''}
        />
        {error && <small className="field-error" id={`${id}Error`}>{error}</small>}
      </div>
    );
  };

  return (
    <div className="quote-overlay" onClick={(event) => event.target === event.currentTarget && closeCheckout()} role="presentation">
      <div aria-labelledby="quoteContactTitle" aria-modal="true" className="quote-contact-modal" role="dialog">
        <div className="quote-panel-header">
          <div>
            <h2 id="quoteContactTitle">
              {isProfileCompletion
                ? t('Complete your details', 'أكمل بياناتك')
                : t('Contact Details', 'بيانات التواصل')}
            </h2>
            <p>
              {isProfileCompletion
                ? t('Add the missing details to submit your quote request.', 'أضف البيانات الناقصة لإرسال طلب عرض السعر.')
                : t('Enter your details to submit your quote request.', 'أدخل بياناتك لإرسال طلب عرض السعر.')}
            </p>
          </div>
          <button aria-label="Close checkout" className="quote-close" onClick={closeCheckout} type="button">&times;</button>
        </div>
        <form noValidate onSubmit={submit}>
          {renderField('name', t('Full Name', 'الاسم الكامل'), 'name', 'text')}
          {renderField('email', t('Email', 'البريد الإلكتروني'), 'email', 'email')}
          {renderField('phone', t('Phone Number', 'رقم الهاتف'), 'tel', 'tel')}
          <button className="btn2 quote-checkout-button" disabled={!canSubmit || submitting} type="submit">
            {uploading
              ? t('Uploading files...', 'جاري رفع الملفات...')
              : submitting
                ? t('Submitting...', 'جاري الإرسال...')
                : t('Submit Quote Request', 'إرسال طلب عرض السعر')}
          </button>
        </form>
      </div>
    </div>
  );
}

function QuoteUploadProgress({ progress }) {
  const { t } = useLanguage();
  if (!progress) return null;

  const percentage = Math.round((progress.completed / progress.total) * 100);

  return (
    <div className="quote-upload-progress" role="status">
      <div>
        <strong>{t('Uploading design files...', 'جاري رفع ملفات التصميم...')}</strong>
        <span>{progress.completed} / {progress.total}</span>
      </div>
      <p>{progress.currentFile}</p>
      <div className="quote-upload-progress-track" aria-hidden="true">
        <span style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

export function QuoteCartProvider({ children }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [cart, setCart] = useState(readCart);
  const [editingItem, setEditingItem] = useState(readEditingQuote);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [requiredContactFields, setRequiredContactFields] = useState(CONTACT_FIELDS);
  const [contactDefaults, setContactDefaults] = useState(EMPTY_CONTACT);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch {
      setNotice({
        type: 'error',
        text: t('The quote cart could not be saved in this browser.', 'تعذر حفظ سلة عروض الأسعار في هذا المتصفح.'),
      });
    }
  }, [cart, t]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const addItem = (item) => {
    const isUpdating = editingItem && editingQuotePath(editingItem) === editingQuotePath(item);
    let nextItem = item;

    if (isUpdating) {
      const selectedFiles = getQuoteUploadFiles(item.id);
      if (selectedFiles.length) setQuoteUploadFiles(editingItem.id, selectedFiles);
      clearQuoteUploadFiles(item.id);
      nextItem = { ...item, id: editingItem.id };
    }

    setCart((current) => (
      isUpdating
        ? current.map((cartItem) => cartItem.id === editingItem.id ? nextItem : cartItem)
        : [...current, nextItem]
    ));
    if (isUpdating) {
      clearEditingQuote();
      setEditingItem(null);
    }
    setNotice({ type: 'success', text: t('Added to quote cart.', 'تمت الإضافة إلى سلة عروض الأسعار.') });
  };

  const removeItem = (id) => {
    clearQuoteUploadFiles(id);
    setCart((current) => current.filter((item) => item.id !== id));
    if (editingItem?.id === id) {
      clearEditingQuote();
      setEditingItem(null);
    }
  };

  const editItem = (item) => {
    const path = editingQuotePath(item);
    if (!path.startsWith('/')) {
      setNotice({ type: 'error', text: t('This cart item cannot be edited.', 'لا يمكن تعديل هذا العنصر.') });
      return;
    }

    saveEditingQuote(item);
    setEditingItem(item);
    setCartOpen(false);
    navigate(`${path}#configuration`);
    window.dispatchEvent(new CustomEvent('quoteedit'));
  };

  const showInvalidQuantityNotice = () => {
    setNotice({
      type: 'error',
      text: t('Please enter a valid quantity before submitting.', 'يرجى إدخال كمية صحيحة قبل الإرسال.'),
    });
  };

  const quoteErrorMessage = (error, fallback) => getPublicErrorMessage(error, {
    email: t('Please enter a valid email address.', 'يرجى إدخال بريد إلكتروني صحيح.'),
    name: t('Please enter your full name.', 'يرجى إدخال الاسم الكامل.'),
    network: t('Please check your connection and try again.', 'يرجى التحقق من الاتصال والمحاولة مرة أخرى.'),
    phone: t(
      'Please enter a valid phone number with country code, e.g. +20109878708.',
      'يرجى إدخال رقم هاتف صحيح مع رمز الدولة، مثال: +20109878708.',
    ),
    quantity: t('Please enter a valid quantity before submitting.', 'يرجى إدخال كمية صحيحة قبل الإرسال.'),
    upload: t('The design files could not be uploaded. Please try again.', 'تعذر رفع ملفات التصميم. يرجى المحاولة مرة أخرى.'),
  }, fallback);

  const submitItems = async (items, contact) => {
    if (!items.length) return false;

    const contactValidation = validateCustomerDetails(contact);
    if (!contactValidation.isValid) {
      setNotice({
        type: 'error',
        text: t('Please provide valid customer details before submitting.', 'يرجى إدخال بيانات عميل صحيحة قبل الإرسال.'),
      });
      return false;
    }

    if (items.some((item) => !hasValidQuoteQuantity(item))) {
      showInvalidQuantityNotice();
      return false;
    }

    const uploadValidationError = quoteUploadValidationError(items);
    if (uploadValidationError) {
      setNotice({ type: 'error', text: uploadValidationError });
      return false;
    }

    setSubmitting(true);
    let uploadedPaths = [];
    try {
      const uploadResult = await uploadQuoteDesignFiles(items, supabaseClient.storage, setUploadProgress);
      uploadedPaths = uploadResult.paths;
      const requests = createQuoteRequestRows(uploadResult.items, contactValidation.normalized);
      const { error } = await supabaseClient.from('quote_requests').insert(requests);
      if (error) throw error;
    } catch (error) {
      if (uploadedPaths.length) {
        await removeQuoteUploads(supabaseClient.storage, uploadedPaths).catch(() => {});
      }
      setNotice({
        type: 'error',
        text: quoteErrorMessage(error, t('The quote request could not be submitted. Please try again.', 'تعذر إرسال طلب عرض السعر. يرجى المحاولة مرة أخرى.')),
      });
      return false;
    } finally {
      setUploadProgress(null);
      setSubmitting(false);
    }

    const submittedIds = new Set(items.map((item) => item.id));
    submittedIds.forEach(clearQuoteUploadFiles);
    setCart((current) => current.filter((item) => !submittedIds.has(item.id)));
    setPendingItems([]);
    setPendingUserId(null);
    setCheckoutOpen(false);
    setCartOpen(false);
    setNotice({ type: 'success', text: t('Your quote request was submitted.', 'تم إرسال طلب عرض السعر.') });
    return true;
  };

  const checkoutItems = async (items) => {
    if (!items.length) return;

    if (items.some((item) => !hasValidQuoteQuantity(item))) {
      showInvalidQuantityNotice();
      return;
    }

    const uploadValidationError = quoteUploadValidationError(items);
    if (uploadValidationError) {
      setNotice({ type: 'error', text: uploadValidationError });
      return;
    }

    setSubmitting(true);

    try {
      const { data: authData, error: authError } = await supabaseClient.auth.getUser();
      const authUser = authData?.user || null;

      if (authError && user) throw authError;

      if (!authUser) {
        setContactDefaults(EMPTY_CONTACT);
        setRequiredContactFields(CONTACT_FIELDS);
        setPendingUserId(null);
        setPendingItems(items);
        setCheckoutOpen(true);
        return;
      }

      const { data: profile, error: profileError } = await supabaseClient
        .from('profiles')
        .select('id, email, full_name, phone')
        .eq('id', authUser.id)
        .maybeSingle();

      if (profileError) throw profileError;

      const validation = validateCustomerDetails({
        name: profile?.full_name || '',
        email: authUser.email || profile?.email || '',
        phone: profile?.phone || '',
      });

      if (validation.isValid) {
        await submitItems(items, validation.normalized);
        return;
      }

      setContactDefaults(validation.normalized);
      setRequiredContactFields(Object.keys(validation.errors));
      setPendingUserId(authUser.id);
      setPendingItems(items);
      setCheckoutOpen(true);
    } catch (error) {
      setNotice({
        type: 'error',
        text: quoteErrorMessage(error, t('Your customer details could not be loaded. Please try again.', 'تعذر تحميل بيانات العميل. يرجى المحاولة مرة أخرى.')),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const completeCheckout = async (contact) => {
    const validation = validateCustomerDetails(contact);
    if (!validation.isValid) return;

    if (pendingUserId) {
      setSubmitting(true);

      try {
        const { error } = await supabaseClient
          .from('profiles')
          .update({
            email: validation.normalized.email,
            full_name: validation.normalized.name,
            phone: validation.normalized.phone,
          })
          .eq('id', pendingUserId);

        if (error) throw error;
      } catch (error) {
        setNotice({
          type: 'error',
          text: quoteErrorMessage(error, t('Your customer details could not be saved. Please try again.', 'تعذر حفظ بيانات العميل. يرجى المحاولة مرة أخرى.')),
        });
        return;
      } finally {
        setSubmitting(false);
      }
    }

    await submitItems(pendingItems, validation.normalized);
  };

  const value = {
    addItem,
    cart,
    cancelEditing: () => {
      clearEditingQuote();
      setEditingItem(null);
    },
    checkoutItem: (item) => checkoutItems([item]),
    closeCart: () => setCartOpen(false),
    editingItem,
    openCart: () => setCartOpen(true),
    submitting,
    uploadProgress,
  };

  return (
    <QuoteCartContext.Provider value={value}>
      {children}
      <QuoteCartDrawer
        cart={cart}
        checkoutCart={() => checkoutItems(cart)}
        closeCart={() => setCartOpen(false)}
        editItem={editItem}
        isOpen={cartOpen}
        removeItem={removeItem}
        submitting={submitting}
        uploading={Boolean(uploadProgress)}
      />
      <ContactCheckoutModal
        closeCheckout={() => setCheckoutOpen(false)}
        contact={contactDefaults}
        fields={requiredContactFields}
        isOpen={checkoutOpen}
        isProfileCompletion={Boolean(pendingUserId)}
        submitCheckout={completeCheckout}
        submitting={submitting}
        uploading={Boolean(uploadProgress)}
      />
      <QuoteUploadProgress progress={uploadProgress} />
      {notice && <div className={`quote-notice ${notice.type}`} role="status">{notice.text}</div>}
    </QuoteCartContext.Provider>
  );
}

export function useQuoteCart() {
  const context = useContext(QuoteCartContext);
  if (!context) throw new Error('useQuoteCart must be used inside QuoteCartProvider');
  return context;
}
